import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, Check, X, Zap, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const TIERS = [
  {
    id: "free", name: "Free", price: "$0", note: "current plan",
    features: ["1 learner", "25 stimulus cards", "Card library + trial runner"],
    Icon: Sparkles,
  },
  {
    id: "solo", name: "Solo BCBA", price: "$19", note: "per month · 7-day trial",
    features: ["Unlimited learners & cards", "Match / Sort activities", "AI photo + illustration gen", "Concept auto-pairing"],
    Icon: Zap,
    highlighted: true,
  },
  {
    id: "clinic", name: "Clinic", price: "$79", note: "per month · up to 10 seats",
    features: ["Everything in Solo", "Team sharing + roles", "Bulk illustration regen", "Concept auto-pairing"],
    Icon: Users,
  },
];

export default function UpgradeModal() {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    const handler = (e) => {
      setDetail(e.detail || null);
      setOpen(true);
    };
    window.addEventListener("upgrade-required", handler);
    return () => window.removeEventListener("upgrade-required", handler);
  }, []);

  const goto = () => {
    setOpen(false);
    nav("/billing");
  };

  const message = detail?.message || "You've hit your Free plan's limit.";
  const resource = detail?.resource || "resource";
  const limit = detail?.limit;
  const current = detail?.current;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="upgrade-modal">
        <DialogHeader>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Upgrade required</div>
          <DialogTitle className="font-display text-2xl">Ready to grow beyond Free?</DialogTitle>
          <DialogDescription>{message}</DialogDescription>
        </DialogHeader>

        {limit != null && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900 mb-2" data-testid="upgrade-modal-details">
            You have <strong>{current}</strong> {resource} — the Free plan allows <strong>{limit}</strong>.
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-3">
          {TIERS.map((t) => {
            const Icon = t.Icon;
            return (
              <div
                key={t.id}
                data-testid={`upgrade-tier-${t.id}`}
                className={`rounded-2xl border p-5 flex flex-col ${
                  t.highlighted
                    ? "bg-slate-900 text-white border-slate-900 shadow-lg -translate-y-1"
                    : "bg-white text-slate-900 border-slate-200"
                }`}
              >
                <div className="text-xs font-bold uppercase tracking-[0.14em] opacity-70 flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5" /> {t.name}
                </div>
                <div className="mt-2 font-display text-3xl font-extrabold">{t.price}</div>
                <div className={`text-[11px] mt-0.5 ${t.highlighted ? "text-slate-300" : "text-slate-500"}`}>{t.note}</div>
                <ul className="mt-4 space-y-1.5 text-xs flex-1">
                  {t.features.map((f) => (
                    <li key={f} className="flex items-start gap-1.5">
                      <Check className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${t.highlighted ? "text-emerald-400" : "text-emerald-600"}`} />
                      <span className={t.highlighted ? "text-slate-100" : "text-slate-700"}>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full" data-testid="upgrade-modal-close">
            <X className="w-4 h-4 mr-1" /> Not now
          </Button>
          <Button onClick={goto} className="rounded-full" data-testid="upgrade-modal-cta">
            See plans & upgrade
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
